import { logger } from '../services/logger';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Market } from '../types';
import { MarketEngine } from '../lib/marketEngine';
import { formatMarketResolutionCountdown } from '../lib/marketResolutionDisplay';
import { Plus, BarChart3, Calendar, Bell, FileText, DollarSign, Wallet, CreditCard, Settings, Users, Shield, Key, Code } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useSettings } from '../lib/settings/settingsContext';
import { supabase } from '../lib/db';
import { Leaderboard } from '../components/Leaderboard';
import { AffiliateLeaderboard } from '../components/AffiliateLeaderboard';

const marketEngine = new MarketEngine();

export function AdminDashboard() {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSystemConfigPermission, setHasSystemConfigPermission] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch markets
        const dbMarkets = await marketEngine.getAllMarkets();
        
        // Process markets to ensure they are properly formatted
        const processedMarkets = await Promise.all(
          dbMarkets.map(async (market) => {
            try {
              // Convert market to display format with the engine
              const displayMarket = await marketEngine.toDisplayMarket(market);
              
              // Ensure outcomes is always an array
              if (!Array.isArray(displayMarket.outcomes)) {
                displayMarket.outcomes = [];
                logger.warn(`Market ${displayMarket.id} has invalid outcomes format`);
              }
              
              // Validate each outcome has required properties with proper values
              displayMarket.outcomes = displayMarket.outcomes.map(outcome => {
                return {
                  name: outcome.name || 'Unknown',
                  probability: typeof outcome.probability === 'number' ? outcome.probability : 0,
                  odds: typeof outcome.odds === 'number' ? outcome.odds : 0,
                  yesBets: typeof outcome.yesBets === 'number' ? outcome.yesBets : 0
                };
              });
              
              return displayMarket;
            } catch (err) {
              logger.error(`Error processing market ${market.id}:`, err);
              // Return a basic market with empty outcomes array to prevent errors
              return {
                ...market,
                outcomes: [],
                timeLeft: formatMarketResolutionCountdown(market.resolution_date ?? null)
              };
            }
          })
        );
        
        setMarkets(processedMarkets);
        
        // Check for system configuration permission
        if (user) {
          const { data } = await supabase
            .from('user_permissions')
            .select('*')
            .eq('user_id', user.id)
            .eq('permission', 'system_configuration')
            .maybeSingle();
            
          setHasSystemConfigPermission(!!data);
        }
        
        setError(null);
      } catch (error) {
        logger.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Check if user has admin privileges
  const isUserAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
  const isSuperUser = user?.app_metadata?.role === 'superuser';

  if (!isUserAdmin) {
    navigate('/markets');
    return null;
  }

  if (loading) {
    return (
      <div className="py-8 px-4 md:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
          </div>
          <p className="text-center text-gray-500 mt-4">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 px-4 md:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="text-center text-red-600 font-medium">{error}</div>
          <p className="text-center text-gray-500 mt-2">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 md:px-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {/* <Link
            to="/admin/market-requests"
            className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1.5 rounded-md hover:bg-opacity-90 transition-colors text-sm"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Requests</span>
          </Link> */}
          {/* <Link
            to="/admin/notifications"
            className="flex items-center gap-1 bg-purple-600 text-white px-2 py-1.5 rounded-md hover:bg-opacity-90 transition-colors text-sm"
          >
            <Bell className="w-3.5 h-3.5" />
            <span>Notifications</span>
          </Link> */}
          <button
            onClick={() => navigate('/admin/create-market')}
            className="flex items-center gap-1 bg-wayja-teal text-white px-2 py-1.5 rounded-md hover:bg-opacity-90 transition-colors text-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Create Market</span>
          </button>
        </div>
      </div>
      
      {/* Admin Tools Section */}
      <div className="mb-8">
        <h2 className="text-lg sm:text-xl font-bold mb-4">Admin Tools</h2>
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-x divide-y divide-gray-100">
            <Link
              to="/admin/markets"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center text-green-600 mr-3 flex-shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Market Dashboard</h3>
                <p className="text-xs text-gray-500">Manage active markets</p>
              </div>
            </Link>
            
            <Link
              to="/admin/metrics"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mr-3 flex-shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Platform Metrics</h3>
                <p className="text-xs text-gray-500">Analytics & reporting</p>
              </div>
            </Link>
            
            <Link
              to="/admin/manage"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">User Management</h3>
                <p className="text-xs text-gray-500">Manage user accounts</p>
              </div>
            </Link>
            
            <Link
              to="/admin/developers"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                <Code className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Developer Accounts</h3>
                <p className="text-xs text-gray-500">Manage developer accounts & API keys</p>
              </div>
            </Link>
            
            <Link
              to="/admin/monitoring"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center text-red-600 mr-3 flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Security Monitoring</h3>
                <p className="text-xs text-gray-500">Monitor security events</p>
              </div>
            </Link>
            
            <Link
              to="/admin/wallet"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 mr-3 flex-shrink-0">
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Wallet Management</h3>
                <p className="text-xs text-gray-500">Manage admin funds</p>
              </div>
            </Link>
            
            <Link
              to="/admin/withdrawals"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center text-red-600 mr-3 flex-shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Withdrawal Requests</h3>
                <p className="text-xs text-gray-500">Process withdrawals</p>
              </div>
            </Link>
            
            <Link
              to="/admin/fica"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-wayja-teal/20 rounded-full flex items-center justify-center text-wayja-teal mr-3 flex-shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">FICA Verification</h3>
                <p className="text-xs text-gray-500">Review FICA documents</p>
              </div>
            </Link>
            
            <Link
              to="/admin/transactions"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Transactions</h3>
                <p className="text-xs text-gray-500">View all transactions</p>
              </div>
            </Link>
            
            {/* <Link
              to="/admin/notifications"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mr-3 flex-shrink-0">
                <Bell className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Notifications</h3>
                <p className="text-xs text-gray-500">Manage notifications</p>
              </div>
            </Link> */}
            
            <Link
              to="/admin/site"
              className="flex items-center p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mr-3 flex-shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Site Management</h3>
                <p className="text-xs text-gray-500">Manage content</p>
              </div>
            </Link>
            
            {(isUserAdmin || hasSystemConfigPermission) && (
              <Link
                to="/admin/settings"
                className="flex items-center p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                  <Settings className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">System Settings</h3>
                  <p className="text-xs text-gray-500">Configure settings</p>
                </div>
              </Link>
            )}

            {isUserAdmin && (
              <Link
                to="/admin/fees"
                className="flex items-center p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mr-3 flex-shrink-0">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Revenue & Fees</h3>
                  <p className="text-xs text-gray-500">Configure spread and fee settings</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
      
      {/* Leaderboard Section */}
      <div className="mb-8 space-y-6">
        <div>
          <h2 className="text-lg sm:text-xl font-bold mb-4">Profit Leaderboard</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <Leaderboard limit={20} showDetails={true} />
          </div>
        </div>
        <div>
          <h2 className="text-lg sm:text-xl font-bold mb-4">Affiliate Leaderboard</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <AffiliateLeaderboard limit={20} showDetails={true} />
          </div>
        </div>
      </div>
      
      {/* Markets Section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg sm:text-xl font-bold">Recent Markets</h2>
          {markets.length > 4 && (
            <Link
              to="/admin/markets"
              className="text-wayja-teal hover:underline text-sm flex items-center"
            >
              View all <span className="ml-1">→</span>
            </Link>
          )}
        </div>
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {markets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market Size</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Traders</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {markets.slice(0, 5).map(market => (
                    <tr 
                      key={market.id} 
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/market/${market.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 mr-3">
                            {market.image_url ? (
                              <img
                                className="h-10 w-10 rounded object-cover"
                                src={market.image_url}
                                alt=""
                              />
                            ) : (
                              <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                                <span className="text-xs text-gray-500">{market.category?.substring(0, 2) || "N/A"}</span>
                              </div>
                            )}
                          </div>
                          <div className="max-w-xs">
                            <div className="text-sm font-medium text-gray-900 truncate" title={market.title}>
                              {market.title}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex text-xs font-medium bg-gray-100 text-gray-800 py-0.5 px-2 rounded-sm">
                          {market.category || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {market.status === 'active' && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                            Live
                          </span>
                        )}
                        {market.status === 'resolved' && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            Resolved
                          </span>
                        )}
                        {market.status === 'cancelled' && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            Cancelled
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {settings.currency.symbol}{market.totalPool ? market.totalPool.toLocaleString('en-US', {maximumFractionDigits: 0}) : 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {market.unique_traders || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {market.timeLeft || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No markets available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}